import { type ComputedRef, computed, ref } from 'vue';
import { useRoute } from 'vue-router';
import type { Opportunity } from '~/interfaces/opportunities/main';
import { useOpportunityQuery } from '~/composables/opportunities/useOpportunitiesQuery';

export interface UseOpportunityDetailOptions {
  /** Enable the query immediately if true. Defaults to true. */
  immediate?: boolean;
}

export interface UseOpportunityDetailResult {
  opportunity: ComputedRef<Opportunity | null>;
  loading: ComputedRef<boolean>;
  error: ComputedRef<boolean>;
  fetchOpportunity: (hashId?: string | null) => Promise<void>;
  setOpportunityData: (value: Opportunity | null) => void;
  query: ReturnType<typeof useOpportunityQuery>['query'];
  hashId: ComputedRef<string>;
}

export const useOpportunityDetail = (
  options: UseOpportunityDetailOptions = { immediate: true },
): UseOpportunityDetailResult => {
  const route = useRoute();
  const enabled = ref<boolean>(options.immediate ?? true);

  const hashId = computed<string>(() => {
    const routeParam = route?.params?.opportunityHashId?.toString();
    if (routeParam) { return routeParam; }
    const queryParam = route?.query?.hashId as string | undefined;
    return queryParam ?? '';
  });

  const {
    query,
    opportunity,
    setOpportunity,
  } = useOpportunityQuery(hashId, { enabled });

  const loading = computed<boolean>(() =>
    Boolean(query.isFetching?.value || query.isPending?.value),
  );

  const error = computed<boolean>(() => Boolean(query.error?.value));

  const fetchOpportunity = async () => {
    enabled.value = true;
    if (!hashId.value) {
      setOpportunity(null);
      return;
    }
    await query.refetch();
  };

  const setOpportunityData = (value: Opportunity | null): void => {
    const targetHash = value?.hashId ?? hashId.value;
    setOpportunity(value, targetHash);
  };

  return {
    opportunity,
    loading,
    error,
    fetchOpportunity,
    setOpportunityData,
    query,
    hashId,
  };
};
